import * as React from 'react';
import { cn } from '../../lib/utils';
import { Button } from './button';

/**
 * 관리자 목록 화면 공통 스캐폴딩 프리미티브.
 * SectionHeader · SearchBar · SortableTh · RowActions · TableShell · Pagination · 상태.
 * 모두 Tailwind 토큰 클래스 → 7개 테마 자동 반응.
 */

// ─── 헤더 (제목 + 건수 + 추가 버튼) ─────────────────────────────
export const SectionHeader: React.FC<{
  title: string;
  total?: number;
  onAdd?: () => void;
  addLabel?: string;
  description?: string;
}> = ({ title, total, onAdd, addLabel = '+ 추가', description }) => (
  <>
    <div className="mb-2 flex items-center justify-between">
      <div className="flex items-baseline gap-3">
        <h2 className="m-0 text-xl font-bold text-foreground">{title}</h2>
        {typeof total === 'number' && (
          <span className="text-[0.82rem] font-medium text-muted-foreground">총 {total.toLocaleString()}건</span>
        )}
      </div>
      {onAdd && (
        <Button type="button" size="sm" onClick={onAdd}>
          {addLabel}
        </Button>
      )}
    </div>
    {description && <p className="mb-4 text-sm text-muted-foreground">{description}</p>}
  </>
);

// ─── 검색/필터 바 컨테이너 ──────────────────────────────────────
export const SearchBar: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cn('mb-5 rounded-lg border border-border bg-muted/30 px-4 py-3.5', className)}>
    <div className="flex flex-wrap items-center gap-2.5">{children}</div>
  </div>
);

// ─── 테이블 셸 (컨테이너 + thead + 선택적 footer) ───────────────
export const TableShell: React.FC<{
  headers: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ headers, children, footer }) => (
  <div className="overflow-hidden rounded-lg border border-border">
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[0.82rem]">
        <thead>
          <tr className="border-b-2 border-border bg-muted">{headers}</tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
    {footer}
  </div>
);

const thBase = 'whitespace-nowrap px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wide text-muted-foreground';

/** 정렬 불가 헤더 */
export const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <th className={cn(thBase, className)}>{children}</th>
);

/** 정렬 가능 헤더 — 클릭 시 방향 토글 표시 */
export const SortableTh: React.FC<{
  label: string;
  sortKey: string;
  activeKey?: string | null;
  dir?: 'asc' | 'desc';
  onSort: (k: string) => void;
}> = ({ label, sortKey, activeKey, dir, onSort }) => {
  const active = activeKey === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn(thBase, 'cursor-pointer select-none', active && 'text-primary')}
      title="클릭하여 정렬"
    >
      {label}{' '}
      <span className={cn('text-[0.7rem]', active ? 'text-primary' : 'text-muted-foreground/50')}>
        {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );
};

/** 지브라 행 (idx 짝/홀 배경) */
export const Row: React.FC<{ idx: number; children: React.ReactNode }> = ({ idx, children }) => (
  <tr className={cn('border-b border-border', idx % 2 === 0 ? 'bg-card' : 'bg-muted/30', 'hover:bg-muted/50')}>
    {children}
  </tr>
);

const tdBase = 'px-3 py-2 align-middle';
export const Td: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <td className={cn(tdBase, className)}>{children}</td>
);

// ─── 행 관리 버튼 (수정/삭제/복제) ───────────────────────────────
export const RowActions: React.FC<{ onEdit: () => void; onDelete: () => void; onDuplicate?: () => void }> = ({
  onEdit,
  onDelete,
  onDuplicate,
}) => (
  <div className="flex gap-1.5">
    <button
      type="button"
      onClick={onEdit}
      className="rounded border border-border px-2 py-0.5 text-[0.72rem] font-medium text-foreground hover:bg-muted"
    >
      수정
    </button>
    <button
      type="button"
      onClick={onDelete}
      className="rounded border border-destructive/40 px-2 py-0.5 text-[0.72rem] font-medium text-destructive hover:bg-destructive/10"
    >
      삭제
    </button>
    {onDuplicate && (
      <button
        type="button"
        onClick={onDuplicate}
        className="rounded border border-border px-2 py-0.5 text-[0.72rem] font-medium text-foreground hover:bg-muted"
      >
        복제
      </button>
    )}
  </div>
);

// ─── 로딩 / 빈 상태 ─────────────────────────────────────────────
export const LoadingState: React.FC<{ text?: string }> = ({ text = '불러오는 중...' }) => (
  <div className="p-10 text-center text-muted-foreground">{text}</div>
);

export const EmptyState: React.FC<{ text?: string }> = ({ text = '데이터가 없습니다.' }) => (
  <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground">{text}</div>
);

// ─── 페이징 ─────────────────────────────────────────────────────
export const Pagination: React.FC<{ page: number; totalPages: number; onPage: (p: number) => void }> = ({
  page,
  totalPages,
  onPage,
}) => {
  if (totalPages <= 1) return null;
  const navBtn = (label: string, target: number, disabled: boolean) => (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="h-7 px-2.5"
      disabled={disabled}
      onClick={() => onPage(target)}
    >
      {label}
    </Button>
  );
  return (
    <div className="flex items-center justify-center gap-2 border-t border-border bg-card px-3 py-3">
      {navBtn('«', 1, page === 1)}
      {navBtn('‹', Math.max(1, page - 1), page === 1)}
      <span className="px-2 text-[0.82rem] font-medium text-foreground">
        {page} / {totalPages}
      </span>
      {navBtn('›', Math.min(totalPages, page + 1), page === totalPages)}
      {navBtn('»', totalPages, page === totalPages)}
    </div>
  );
};
